import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { Badge, Box, IconButton, SvgIcon } from '@material-ui/core';
import { MDSButton, MDSTheme, MDSTypography } from '@marqvision/mds/core';
import MDSDropDown, { ISelectValue } from '@marqvision/mds/core/DropDown';
import { AccountOutlineIcon, LanguageIcon, NotificationOutlineIcon } from '@marqvision/mds/icons';
import { useAuth } from '../../apis/auth';
import { useNoti } from '../../apis/home';
import useGlobalAction from '../../hooks/useGlobalAction';
import useLanguage from '../../hooks/useLanguage';
import useRouteNames from '../../hooks/useRouteNames';
import { LanguageEnum } from '../../LocalizedStrings';
import { Logger } from '../../utils';
import { latestScrollY } from '../../atoms/myApp';
import { HeaderContext } from '.';

interface IProps {
  hideCurrency?: boolean;
  onLoginClick?: () => void;
  size?: 'medium' | 'large';
  notification?: boolean;
}

const Control = ({
  hideCurrency: hideCurrencyProps,
  onLoginClick,
  size = 'large',
  ...restProps
}: IProps): JSX.Element => {
  const RouteNames = useRouteNames();

  const {
    variant = 'normal',
    paddingHorizontal = '24px',
    notification,
    hideLoginOrProfile,
    hideCurrency = hideCurrencyProps,
    hideChangeLanguage,
  } = useContext(HeaderContext);

  const { data: authData } = useAuth();
  const isLoggedIn = Boolean(authData?.accessToken);

  // const { data: notiData } = useNoti(isLoggedIn);
  const { Lang, language, setLanguage } = useLanguage();
  const { login } = useGlobalAction();
  const setScrollY = useSetAtom(latestScrollY);

  const getButtonColor = (variant: 'normal' | 'transparent' | 'dark') => (variant === 'normal' ? 'bluegray' : 'white');

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      gridGap={size === 'medium' ? '16px' : '20px'}
      flexBasis="200px"
      style={{
        position: 'absolute',
        right: size === 'medium' ? '12px' : paddingHorizontal,
      }}
    >
      {!hideLoginOrProfile && isLoggedIn && (
        <>
          <Link to={RouteNames.Profile} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            {size === 'medium' ? (
              <SvgIcon style={{ width: '16px', height: '16px' }}>
                <AccountOutlineIcon />
              </SvgIcon>
            ) : (
              <MDSButton
                startIcon={<AccountOutlineIcon />}
                variant="text"
                color={getButtonColor(variant)}
                size="medium"
              >
                {Lang.Profile}
              </MDSButton>
            )}
          </Link>
        </>
      )}
      {!hideLoginOrProfile && !isLoggedIn && (
        <MDSButton
          onClick={() => {
            onLoginClick && onLoginClick();
            login();
          }}
          startIcon={<AccountOutlineIcon />}
          variant="text"
          color={getButtonColor(variant)}
          size="medium"
        >
          {Lang.Login}
        </MDSButton>
      )}

      {!hideCurrency && (size === 'medium' || isLoggedIn) && (
        <MDSTypography
          variant={size === 'medium' ? 'T14' : 'T14'}
          weight="medium"
          style={{
            color: getButtonColor(variant) === 'white' ? MDSTheme.palette.white : MDSTheme.palette.bluegray[800],
          }}
        >
          $ - USD
        </MDSTypography>
      )}

      {!hideChangeLanguage && (
        <MDSDropDown
          width="100%"
          list={[
            { value: LanguageEnum.English, label: 'English' },
            { value: LanguageEnum.Korean, label: '한국어' },
          ]}
          value={language}
          onChange={(value) => {
            Logger.track('changed language', { From: language, To: value });
            setLanguage(value as LanguageEnum);
          }}
          renderAnchorComponent={(selectedItem, open) => {
            return (
              <MDSButton
                startIcon={<LanguageIcon />}
                variant="text"
                color={getButtonColor(variant)}
                size="medium"
                onClick={open}
              >
                {(selectedItem as ISelectValue).label || ''}
              </MDSButton>
            );
          }}
        />
      )}
    </Box>
  );
};

export default Control;
